import { api } from "@services/api";
import type {
  ApproveTerminalServiceRequestPayload,
  BerthLocation,
  CancelTerminalServiceRequestPayload,
  CloseBypassServiceRequestPayload,
  CreateBypassServiceRequestPayload,
  CreateGantryCraneServiceRequestPayload,
  CreateNitrogenSupplyServiceRequestPayload,
  CreatePiggingServiceRequestPayload,
  FottServiceResponse,
  FottServiceSearchRequestPayload,
  RejectTerminalServiceRequestPayload,
  SubmitTerminalServiceUsageHoursPayload,
  TerminalServiceCompany,
  TerminalServiceCompanyFilterKey,
  TerminalServiceDetailsResponse,
} from "@services/terminal-services/types";
import { FottServiceType } from "@services/terminal-services/types";

const SERVICE = "bff-terminal-service";
const COMPANIES_PATH = "companies";
const SUBMIT_TERMINAL_SERVICE_REQUEST_PATH = "terminal-services/submit";
const SEARCH_TERMINAL_SERVICES_PATH = "terminal-services/search";
const RESUBMIT_TERMINAL_SERVICE_REQUEST_ACTION = "resubmit";

type CreateTerminalServiceRequestPayload =
  | CreateGantryCraneServiceRequestPayload
  | CreateNitrogenSupplyServiceRequestPayload
  | CreatePiggingServiceRequestPayload
  | CreateBypassServiceRequestPayload;

const resubmitTerminalServiceRequest = async (
  id: string,
  serviceType: FottServiceType,
  payload: CreateTerminalServiceRequestPayload,
): Promise<void> => {
  return api.post<void>(
    SERVICE,
    `terminal-services/${encodeURIComponent(id)}/${RESUBMIT_TERMINAL_SERVICE_REQUEST_ACTION}?serviceType=${encodeURIComponent(
      serviceType,
    )}`,
    payload,
  );
};

export const resubmitGantryCraneServiceRequest = async ({
  id,
  payload,
}: {
  id: string;
  payload: CreateGantryCraneServiceRequestPayload;
}): Promise<void> => {
  return resubmitTerminalServiceRequest(id, FottServiceType.GANTRY_CRANE_SERVICE, payload);
};

export const resubmitNitrogenSupplyServiceRequest = async ({
  id,
  payload,
}: {
  id: string;
  payload: CreateNitrogenSupplyServiceRequestPayload;
}): Promise<void> => {
  return resubmitTerminalServiceRequest(id, FottServiceType.NITROGEN_SUPPLY_SERVICE, payload);
};

export const resubmitPiggingServiceRequest = async ({
  id,
  payload,
}: {
  id: string;
  payload: CreatePiggingServiceRequestPayload;
}): Promise<void> => {
  return resubmitTerminalServiceRequest(id, FottServiceType.PIGGING_SERVICE, payload);
};

export const resubmitBypassServiceRequest = async ({
  id,
  payload,
}: {
  id: string;
  payload: CreateBypassServiceRequestPayload;
}): Promise<void> => {
  return resubmitTerminalServiceRequest(id, FottServiceType.BYPASS_SERVICE, payload);
};

const submitTerminalServiceRequest = async (
  serviceType: FottServiceType,
  payload: CreateTerminalServiceRequestPayload,
): Promise<void> => {
  return api.post<void>(
    SERVICE,
    `${SUBMIT_TERMINAL_SERVICE_REQUEST_PATH}?serviceType=${encodeURIComponent(serviceType)}`,
    payload,
  );
};

const getTerminalServiceActionPath = ({
  id,
  action,
  serviceType,
}: {
  id: string;
  action: "claim" | "unclaim" | "complete" | "cancel";
  serviceType: FottServiceType;
}) =>
  `terminal-services/${encodeURIComponent(id)}/${action}?serviceType=${encodeURIComponent(
    serviceType,
  )}`;

export const getTerminalServiceCompanies = async (
  filterKey?: TerminalServiceCompanyFilterKey,
): Promise<TerminalServiceCompany[]> => {
  return api.get<TerminalServiceCompany[]>(
    SERVICE,
    `${COMPANIES_PATH}${filterKey ? `?${filterKey}=true` : ""}`,
  );
};

export const getTerminalServiceById = async ({
  id,
  serviceType,
}: {
  id: string;
  serviceType: FottServiceType;
}): Promise<TerminalServiceDetailsResponse> => {
  return api.get<TerminalServiceDetailsResponse>(
    SERVICE,
    `terminal-services/${encodeURIComponent(id)}?serviceType=${encodeURIComponent(serviceType)}`,
  );
};

export const claimTerminalServiceById = async ({
  id,
  serviceType,
}: {
  id: string;
  serviceType: FottServiceType;
}): Promise<void> => {
  return api.post<void>(
    SERVICE,
    getTerminalServiceActionPath({ id, action: "claim", serviceType }),
  );
};

export const unclaimTerminalServiceById = async ({
  id,
  serviceType,
}: {
  id: string;
  serviceType: FottServiceType;
}): Promise<void> => {
  return api.post<void>(
    SERVICE,
    getTerminalServiceActionPath({ id, action: "unclaim", serviceType }),
  );
};

export const rejectTerminalServiceById = async ({
  id,
  serviceType,
  payload,
}: {
  id: string;
  serviceType: FottServiceType;
  payload: RejectTerminalServiceRequestPayload;
}): Promise<void> => {
  return api.post<void>(
    SERVICE,
    getTerminalServiceActionPath({ id, action: "complete", serviceType }),
    payload,
  );
};

export const approveTerminalServiceById = async ({
  id,
  serviceType,
  payload,
}: {
  id: string;
  serviceType: FottServiceType;
  payload: ApproveTerminalServiceRequestPayload;
}): Promise<void> => {
  return api.post<void>(
    SERVICE,
    getTerminalServiceActionPath({ id, action: "complete", serviceType }),
    payload,
  );
};

export const cancelTerminalServiceById = async ({
  id,
  serviceType,
  payload,
}: {
  id: string;
  serviceType: FottServiceType;
  payload: CancelTerminalServiceRequestPayload;
}): Promise<void> => {
  return api.post<void>(
    SERVICE,
    getTerminalServiceActionPath({ id, action: "cancel", serviceType }),
    payload,
  );
};

export const closeBypassServiceById = async ({
  id,
  payload,
}: {
  id: string;
  payload: CloseBypassServiceRequestPayload;
}): Promise<void> => {
  return api.post<void>(SERVICE, `bypass/${encodeURIComponent(id)}/close`, payload);
};

export const submitTerminalServiceUsageHours = async ({
  id,
  serviceType,
  payload,
}: {
  id: string;
  serviceType: FottServiceType;
  payload: SubmitTerminalServiceUsageHoursPayload;
}): Promise<void> => {
  return api.post<void>(
    SERVICE,
    `terminal-services/${encodeURIComponent(id)}/usage-hours?serviceType=${encodeURIComponent(
      serviceType,
    )}`,
    payload,
  );
};

export const updateTerminalServiceUsageHours = async ({
  id,
  serviceType,
  usageHoursId,
  payload,
}: {
  id: string;
  serviceType: FottServiceType;
  usageHoursId: string;
  payload: SubmitTerminalServiceUsageHoursPayload;
}): Promise<void> => {
  return api.patch<void>(
    SERVICE,
    `terminal-services/${encodeURIComponent(id)}/usage-hours/${encodeURIComponent(
      usageHoursId,
    )}?serviceType=${encodeURIComponent(serviceType)}`,
    payload,
  );
};

export const deleteTerminalServiceUsageHours = async ({
  id,
  serviceType,
  usageHoursId,
}: {
  id: string;
  serviceType: FottServiceType;
  usageHoursId: string;
}): Promise<void> => {
  return api.delete<void>(
    SERVICE,
    `terminal-services/${encodeURIComponent(id)}/usage-hours/${encodeURIComponent(
      usageHoursId,
    )}?serviceType=${encodeURIComponent(serviceType)}`,
  );
};

export const createGantryCraneServiceReqest = async (
  payload: CreateGantryCraneServiceRequestPayload,
): Promise<void> => {
  return submitTerminalServiceRequest(FottServiceType.GANTRY_CRANE_SERVICE, payload);
};

export const createNitrogenSupplyServiceReqest = async (
  payload: CreateNitrogenSupplyServiceRequestPayload,
): Promise<void> => {
  return submitTerminalServiceRequest(FottServiceType.NITROGEN_SUPPLY_SERVICE, payload);
};

export const createPiggingServiceRequest = async (
  payload: CreatePiggingServiceRequestPayload,
): Promise<void> => {
  return submitTerminalServiceRequest(FottServiceType.PIGGING_SERVICE, payload);
};

export const createBypassServiceRequest = async (
  payload: CreateBypassServiceRequestPayload,
): Promise<void> => {
  return submitTerminalServiceRequest(FottServiceType.BYPASS_SERVICE, payload);
};

export const searchFottServices = async ({
  page,
  pageSize,
  body,
}: {
  page: number;
  pageSize: number;
  body: FottServiceSearchRequestPayload;
}): Promise<FottServiceResponse> => {
  return api.post<FottServiceResponse>(
    SERVICE,
    `${SEARCH_TERMINAL_SERVICES_PATH}?pageNumber=${page}&pageSize=${pageSize}`,
    body,
  );
};

export const getBerthLocations = async ({
  fromToLocation,
}: {
  fromToLocation: string;
}): Promise<BerthLocation[]> => {
  return api.get<BerthLocation[]>(
    SERVICE,
    `berths?fromToLocation=${encodeURIComponent(fromToLocation)}`,
  );
};
